import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOProps } from './SEO';
import { tokens } from './SEOPageLayout'; // Re-use tokens for consistency initially
import PublicHeader from './PublicHeader';

interface MarketingLayoutProps extends Omit<SEOProps, 'title'> { // Enforce title needed
    title: string;
    h1: string;
    heroText?: string;
    children: ReactNode;
}

/**
 * Standard layout for all marketing, solution, and tool pages.
 * Enforces H1, consistent header/footer, and SEO metadata.
 */
export default function MarketingLayout({
    title,
    h1,
    heroText,
    children,
    ...seoProps
}: MarketingLayoutProps) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            <SEO title={title} {...seoProps} />

            <PublicHeader />

            {/* Main Content */}
            <main>
                {/* Dynamic Hero Section if text provided, otherwise accessible H1 only */}
                {heroText ? (
                    <section className={tokens.section}>
                        <div className={tokens.container}>
                            <div className="max-w-3xl mx-auto text-center">
                                <h1 className={`${tokens.heading.h1} mb-6`}>{h1}</h1>
                                <p className={tokens.text.large}>{heroText}</p>
                            </div>
                        </div>
                    </section>
                ) : (
                    // Visually hidden H1 if page implements its own hero (rare/legacy) -> but better to enforce render
                    // For now, render it at top of container if no heroText, or let children handle layout but enforce H1 existence check?
                    // Design decision: If no heroText, we assume children has Hero. But we MUST ensure H1.
                    // Let's render H1 at top of children container if no heroText passed, OR standard hero.
                    // Simplest for SEO: Standard hero always.
                    <section className={`${tokens.section} pb-0`}>
                        <div className={tokens.container}>
                            <h1 className={`${tokens.heading.h1} mb-6`}>{h1}</h1>
                        </div>
                    </section>
                )}

                {children}
            </main>

            {/* Footer / Hub Links */}
            <footer className="border-t border-slate-800 py-12 mt-24 bg-slate-900/50">
                <div className={tokens.container}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                        {/* Platform Outcomes */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Platform Outcomes</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/continuous-audit-evidence" className="text-slate-400 hover:text-white transition-colors">Continuous Audit Evidence</Link></li>
                                <li><Link to="/github-change-control" className="text-slate-400 hover:text-white transition-colors">Change Control Evidence</Link></li>
                                <li><Link to="/engineering-audit-toil" className="text-slate-400 hover:text-white transition-colors">Engineering Audit Toil</Link></li>
                                <li><Link to="/auditor-self-serve" className="text-slate-400 hover:text-white transition-colors">Auditor Self-Serve</Link></li>
                            </ul>
                        </div>

                        {/* Compliance Frameworks */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Compliance</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/soc2-change-management-evidence" className="text-slate-400 hover:text-white transition-colors">SOC 2 Evidence</Link></li>
                                <li><Link to="/soc2-github-compliance" className="text-slate-400 hover:text-white transition-colors">SOC 2 GitHub Guide</Link></li>
                                <li><Link to="/iso-27001-github-compliance" className="text-slate-400 hover:text-white transition-colors">ISO 27001 GitHub Guide</Link></li>
                                <li><Link to="/solutions/iso-27001-a832-evidence" className="text-slate-400 hover:text-white transition-colors">ISO 27001 A.8.32</Link></li>
                                <li><Link to="/iso-27001-vs-soc2" className="text-slate-400 hover:text-white transition-colors">ISO 27001 vs SOC 2</Link></li>
                                <li><Link to="/eu-ai-act-github-compliance" className="text-slate-400 hover:text-white transition-colors">EU AI Act GitHub Guide</Link></li>
                            </ul>
                        </div>

                        {/* Use Cases & Technical Evidence */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Use Cases</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/github-copilot-compliance" className="text-slate-400 hover:text-white transition-colors">GitHub Copilot Compliance</Link></li>
                                <li><Link to="/ai-assisted-development-evidence" className="text-slate-400 hover:text-white transition-colors">AI-Assisted Development</Link></li>
                                <li><Link to="/github-change-control" className="text-slate-400 hover:text-white transition-colors">GitHub Change Control</Link></li>
                                <li><Link to="/outsourced-software-change-control" className="text-slate-400 hover:text-white transition-colors">Outsourced Development</Link></li>
                                <li><Link to="/solutions" className="text-primary-400 hover:text-primary-300 transition-colors font-medium">View All Solutions →</Link></li>
                            </ul>
                        </div>

                        {/* Features, Tools & Legal */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/features/audit-timeline" className="text-slate-400 hover:text-white transition-colors">Audit Timeline</Link></li>
                                <li><Link to="/features/drift-detection" className="text-slate-400 hover:text-white transition-colors">Drift Detection</Link></li>
                                <li><Link to="/tools" className="text-slate-400 hover:text-white transition-colors">Free Tools</Link></li>
                                <li className="pt-2 border-t border-slate-700 mt-2">
                                    <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy</Link>
                                </li>
                                <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms</Link></li>
                                <li><Link to="/security" className="text-slate-400 hover:text-white transition-colors">Security</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} EvidentTrail. Software change control evidence platform.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
